import React, { useState } from "react";
import { Button, Card, Col, Row, Form, Spinner, InputGroup } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;

    const toastConfig = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    };
    const config = toastConfig[type] || toastConfig.info;

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom">
                    <p className="mb-0">{message}</p>
                </div>
                <div
                    className="toast-progress"
                    style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}
                ></div>
            </div>
        </div>
    );
};

/* ============================================================
   STEPPER MODERNE
   ============================================================ */
const Stepper = ({ step, steps }) => (
    <div className="stepper-modern mb-4">
        <div className="d-flex justify-content-between align-items-center">
            {steps.map((label, index) => {
                const stepNumber = index + 1;
                const isActive = step === stepNumber;
                const isCompleted = step > stepNumber;
                return (
                    <React.Fragment key={index}>
                        <div className={`step-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                            <div className="step-number">
                                {isCompleted ? <i className="ri-check-line"></i> : stepNumber}
                            </div>
                            <div className="step-label">{label}</div>
                        </div>
                        {index < steps.length - 1 && (
                            <div className={`step-line ${step > stepNumber ? 'completed' : ''}`}></div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    </div>
);

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function AjouterClient() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const [formData, setFormData] = useState({
        code_client: '',
        raison_sociale: '',
        type_client: 'entreprise',
        civilite: 'M.',
        nom: '',
        prenom: '',
        adresse: '',
        ville: '',
        code_postal: '',
        pays: 'Gabon',
        telephone: '',
        telephone2: '',
        email: '',
        site_web: '',
        numero_compte: '',
        ice: '',
        patente: '',
        rc: '',
        identifiant_fiscal: '',
        cnss: '',
        forme_juridique: 'SARL',
        plafond_credit: 0,
        solde_initial: 0,
        mode_reglement_defaut: 'especes',
        delai_paiement: 30,
        condition_paiement: 'comptant',
        tarif_applique: 'standard',
        notes: ''
    });

    const showToast = (type, title, message) => {
        setToast({ show: true, type, title, message });
    };

    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.raison_sociale && formData.type_client === 'entreprise') {
            showToast('error', 'Champ requis', 'La raison sociale est obligatoire');
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/clients.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (response.ok && result.success) {
                showToast('success', 'Client créé', 'Le client a été enregistré avec succès');
                setTimeout(() => navigate('/ventes/clients/liste'), 2000);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la création du client");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setLoading(false);
        }
    };

    const nextStep = () => {
        if (step === 1 && !formData.raison_sociale && formData.type_client === 'entreprise') {
            showToast('error', 'Champ requis', 'La raison sociale est obligatoire');
            return;
        }
        setStep(step + 1);
    };

    const prevStep = () => setStep(step - 1);

    /* ============================================================
       RENDU
       ============================================================ */
    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                {/* ===== EN-TÊTE MODERNE ===== */}
                <div className="header-modern mb-4">
                    <div className="header-left">
                        <div className="header-icon-wrapper">
                            <i className="ri-user-add-line"></i>
                        </div>
                        <div>
                            <h5 className="header-title">Nouveau client</h5>
                            <div className="d-flex flex-wrap gap-2 mt-1">
                                <span className="badge-modern">
                                    <i className="ri-store-line"></i>
                                    {formData.type_client === 'entreprise' ? 'Entreprise' : 'Particulier'}
                                </span>
                                <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                    <i className="ri-map-pin-line"></i>
                                    {formData.pays || 'Gabon'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => navigate('/ventes/clients/liste')}
                        className="btn-modern-outline"
                    >
                        <i className="ri-arrow-left-line"></i> Retour
                    </Button>
                </div>

                {/* ===== STEPPER ===== */}
                <Stepper
                    step={step}
                    steps={['Informations générales', 'Identifiants juridiques', 'Paramètres financiers']}
                />

                <Row className="justify-content-center">
                    <Col xl="10">
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header className="card-header-gradient">
                                <div className="d-flex align-items-center">
                                    <div className="client-avatar-large">
                                        <i className="ri-user-add-line"></i>
                                    </div>
                                    <div className="ms-3">
                                        <h5 className="mb-0 text-white">Création d'un nouveau client</h5>
                                        <p className="mb-0 text-white-50 small">
                                            Remplissez toutes les informations nécessaires
                                        </p>
                                    </div>
                                </div>
                            </Card.Header>

                            <Card.Body className="p-4">
                                <Form onSubmit={handleSubmit}>

                                    {/* ===== ÉTAPE 1 : Informations générales ===== */}
                                    {step === 1 && (
                                        <div className="step-content">
                                            <div className="section-title">
                                                <span className="section-icon"><i className="ri-information-line"></i></span>
                                                <h6>Informations générales</h6>
                                            </div>

                                            <Row className="g-3">
                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Type de client</Form.Label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <div className={`type-client-card ${formData.type_client === 'entreprise' ? 'active' : ''}`}>
                                                                <Form.Check
                                                                    type="radio"
                                                                    id="type-entreprise"
                                                                    name="type_client"
                                                                    value="entreprise"
                                                                    checked={formData.type_client === 'entreprise'}
                                                                    onChange={handleChange}
                                                                    label={
                                                                        <span className="d-flex align-items-center gap-2">
                                                                            <i className="ri-building-line"></i> Entreprise
                                                                        </span>
                                                                    }
                                                                />
                                                            </div>
                                                            <div className={`type-client-card ${formData.type_client === 'particulier' ? 'active' : ''}`}>
                                                                <Form.Check
                                                                    type="radio"
                                                                    id="type-particulier"
                                                                    name="type_client"
                                                                    value="particulier"
                                                                    checked={formData.type_client === 'particulier'}
                                                                    onChange={handleChange}
                                                                    label={
                                                                        <span className="d-flex align-items-center gap-2">
                                                                            <i className="ri-user-line"></i> Particulier
                                                                        </span>
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">
                                                            Code client <span className="text-muted small">(optionnel)</span>
                                                        </Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-hashtag"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="text"
                                                                name="code_client"
                                                                value={formData.code_client}
                                                                onChange={handleChange}
                                                                placeholder="Généré automatiquement si vide"
                                                            />
                                                        </InputGroup>
                                                        <Form.Text className="text-muted small">
                                                            Laissez vide pour génération automatique
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>

                                                {formData.type_client === 'entreprise' ? (
                                                    <>
                                                        <Col md="8">
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">
                                                                    Raison sociale <span className="text-danger">*</span>
                                                                </Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text><i className="ri-building-line"></i></InputGroup.Text>
                                                                    <Form.Control
                                                                        type="text"
                                                                        name="raison_sociale"
                                                                        value={formData.raison_sociale}
                                                                        onChange={handleChange}
                                                                        placeholder="Nom de l'entreprise"
                                                                        required
                                                                    />
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md="4">
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">Forme juridique</Form.Label>
                                                                <Form.Select
                                                                    name="forme_juridique"
                                                                    value={formData.forme_juridique}
                                                                    onChange={handleChange}
                                                                    className="input-select-modern"
                                                                >
                                                                    <option value="SARL">SARL</option>
                                                                    <option value="SA">SA</option>
                                                                    <option value="SAS">SAS</option>
                                                                    <option value="EI">Entreprise Individuelle</option>
                                                                    <option value="SNC">SNC</option>
                                                                    <option value="Association">Association</option>
                                                                    <option value="Autre">Autre</option>
                                                                </Form.Select>
                                                            </Form.Group>
                                                        </Col>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Col md="2">
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">Civilité</Form.Label>
                                                                <Form.Select
                                                                    name="civilite"
                                                                    value={formData.civilite}
                                                                    onChange={handleChange}
                                                                    className="input-select-modern"
                                                                >
                                                                    <option value="M.">M.</option>
                                                                    <option value="Mme">Mme</option>
                                                                    <option value="Mlle">Mlle</option>
                                                                </Form.Select>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md="5">
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">Nom</Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                                    <Form.Control
                                                                        type="text"
                                                                        name="nom"
                                                                        value={formData.nom}
                                                                        onChange={handleChange}
                                                                    />
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md="5">
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">Prénom</Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text><i className="ri-user-smile-line"></i></InputGroup.Text>
                                                                    <Form.Control
                                                                        type="text"
                                                                        name="prenom"
                                                                        value={formData.prenom}
                                                                        onChange={handleChange}
                                                                    />
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>
                                                    </>
                                                )}

                                                <Col md="12">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Adresse</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-map-pin-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={2}
                                                                name="adresse"
                                                                value={formData.adresse}
                                                                onChange={handleChange}
                                                                placeholder="Adresse complète"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="4">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Ville</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-building-2-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="text"
                                                                name="ville"
                                                                value={formData.ville}
                                                                onChange={handleChange}
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="4">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Code postal</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-mail-send-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="text"
                                                                name="code_postal"
                                                                value={formData.code_postal}
                                                                onChange={handleChange}
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="4">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Pays</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-earth-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="text"
                                                                name="pays"
                                                                value={formData.pays}
                                                                onChange={handleChange}
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Téléphone</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-phone-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="tel"
                                                                name="telephone"
                                                                value={formData.telephone}
                                                                onChange={handleChange}
                                                                placeholder="+241 XX-XXXXXX"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Téléphone secondaire</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-smartphone-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="tel"
                                                                name="telephone2"
                                                                value={formData.telephone2}
                                                                onChange={handleChange}
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Email</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-mail-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="email"
                                                                name="email"
                                                                value={formData.email}
                                                                onChange={handleChange}
                                                                placeholder="contact@entreprise.com"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Site web</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-global-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="url"
                                                                name="site_web"
                                                                value={formData.site_web}
                                                                onChange={handleChange}
                                                                placeholder="https://"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>
                                    )}

                                    {/* ===== ÉTAPE 2 : Identifiants juridiques ===== */}
                                    {step === 2 && (
                                        <div className="step-content">
                                            <div className="section-title">
                                                <span className="section-icon"><i className="ri-briefcase-line"></i></span>
                                                <h6>Identifiants juridiques</h6>
                                            </div>

                                            <Row className="g-3">
                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Numéro de compte</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-bank-card-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="text"
                                                                name="numero_compte"
                                                                value={formData.numero_compte}
                                                                onChange={handleChange}
                                                                placeholder="N° de compte bancaire"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">ICE (Identifiant Commun des Entreprises)</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-fingerprint-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="text"
                                                                name="ice"
                                                                value={formData.ice}
                                                                onChange={handleChange}
                                                                placeholder="Numéro ICE"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Patente</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-file-list-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="text"
                                                                name="patente"
                                                                value={formData.patente}
                                                                onChange={handleChange}
                                                                placeholder="Numéro de patente"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">RC (Registre de Commerce)</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-book-2-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="text"
                                                                name="rc"
                                                                value={formData.rc}
                                                                onChange={handleChange}
                                                                placeholder="Numéro RC"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Identifiant fiscal (IF)</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-receipt-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="text"
                                                                name="identifiant_fiscal"
                                                                value={formData.identifiant_fiscal}
                                                                onChange={handleChange}
                                                                placeholder="Identifiant fiscal"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">CNSS</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-shield-user-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="text"
                                                                name="cnss"
                                                                value={formData.cnss}
                                                                onChange={handleChange}
                                                                placeholder="Numéro CNSS"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <div className="info-box-modern mt-4">
                                                <div className="info-box-icon">
                                                    <i className="ri-information-line"></i>
                                                </div>
                                                <div>
                                                    <strong>Information</strong>
                                                    <p className="mb-0 small">
                                                        Ces identifiants sont spécifiques aux entreprises.
                                                        Laissez vide si non applicable.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* ===== ÉTAPE 3 : Paramètres financiers ===== */}
                                    {step === 3 && (
                                        <div className="step-content">
                                            <div className="section-title">
                                                <span className="section-icon"><i className="ri-bank-card-line"></i></span>
                                                <h6>Paramètres financiers</h6>
                                            </div>

                                            <Row className="g-3">
                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Plafond de crédit</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-money-euro-circle-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.01"
                                                                name="plafond_credit"
                                                                value={formData.plafond_credit}
                                                                onChange={handleChange}
                                                            />
                                                            <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                                        </InputGroup>
                                                        <Form.Text className="text-muted small">
                                                            0 = pas de crédit autorisé
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Solde initial</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-wallet-3-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.01"
                                                                name="solde_initial"
                                                                value={formData.solde_initial}
                                                                onChange={handleChange}
                                                            />
                                                            <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Mode de règlement par défaut</Form.Label>
                                                        <Form.Select
                                                            name="mode_reglement_defaut"
                                                            value={formData.mode_reglement_defaut}
                                                            onChange={handleChange}
                                                            className="input-select-modern"
                                                        >
                                                            <option value="especes">Espèces</option>
                                                            <option value="cheque">Chèque</option>
                                                            <option value="virement">Virement</option>
                                                            <option value="carte_bancaire">Carte bancaire</option>
                                                            <option value="traite">Traite</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Délai de paiement (jours)</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                type="number"
                                                                name="delai_paiement"
                                                                value={formData.delai_paiement}
                                                                onChange={handleChange}
                                                                min="0"
                                                                max="365"
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Condition de paiement</Form.Label>
                                                        <Form.Select
                                                            name="condition_paiement"
                                                            value={formData.condition_paiement}
                                                            onChange={handleChange}
                                                            className="input-select-modern"
                                                        >
                                                            <option value="comptant">Comptant</option>
                                                            <option value="30j">30 jours fin de mois</option>
                                                            <option value="60j">60 jours fin de mois</option>
                                                            <option value="acompte">Acompte + solde</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Tarif appliqué</Form.Label>
                                                        <Form.Select
                                                            name="tarif_applique"
                                                            value={formData.tarif_applique}
                                                            onChange={handleChange}
                                                            className="input-select-modern"
                                                        >
                                                            <option value="standard">Tarif standard</option>
                                                            <option value="grossiste">Tarif grossiste</option>
                                                            <option value="premium">Tarif premium</option>
                                                            <option value="promo">Promotionnel</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="12">
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Notes / Observations</Form.Label>
                                                        <InputGroup className="input-modern">
                                                            <InputGroup.Text><i className="ri-sticky-note-line"></i></InputGroup.Text>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={3}
                                                                name="notes"
                                                                value={formData.notes}
                                                                onChange={handleChange}
                                                                placeholder="Informations complémentaires..."
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>
                                    )}

                                    <hr className="my-4" style={{ borderColor: '#e2e8f0' }} />

                                    {/* ===== BOUTONS DE NAVIGATION ===== */}
                                    <div className="d-flex justify-content-between flex-wrap gap-2">
                                        <div>
                                            {step > 1 && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={prevStep}
                                                    className="btn-modern-outline"
                                                >
                                                    <i className="ri-arrow-left-line me-2"></i>
                                                    Étape précédente
                                                </Button>
                                            )}
                                        </div>
                                        <div className="d-flex gap-3">
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => navigate('/ventes/clients/liste')}
                                                className="btn-modern-outline"
                                            >
                                                Annuler
                                            </Button>
                                            {step < 3 ? (
                                                <Button
                                                    onClick={nextStep}
                                                    className="btn-modern-primary"
                                                >
                                                    Étape suivante
                                                    <i className="ri-arrow-right-line ms-2"></i>
                                                </Button>
                                            ) : (
                                                <Button
                                                    type="submit"
                                                    disabled={loading}
                                                    className="btn-modern-success"
                                                >
                                                    {loading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" />
                                                            Création en cours...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="ri-check-line me-2"></i>
                                                            Créer le client
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                /* ===== Variables charte ===== */
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== En-tête moderne ===== */
                .header-modern {
                    background: white;
                    padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 1rem;
                }
                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }
                .header-icon-wrapper {
                    width: 52px;
                    height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 14px;
                    color: white;
                    font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title {
                    color: #1e293b;
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0;
                }
                .badge-modern {
                    background: #f1f4f9;
                    color: #475569;
                    padding: 0.3rem 0.8rem;
                    border-radius: 50px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                }
                .client-avatar-large {
                    width: 52px;
                    height: 52px;
                    background: rgba(255,255,255,0.2);
                    border: 2px solid rgba(255,255,255,0.3);
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 1.5rem;
                }

                /* ===== Stepper moderne ===== */
                .stepper-modern {
                    background: white;
                    padding: 1.5rem 2rem;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                }
                .step-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                    z-index: 2;
                    min-width: 80px;
                }
                .step-number {
                    width: 44px;
                    height: 44px;
                    background: #e2e8f0;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    color: #64748b;
                    font-size: 1rem;
                    transition: all 0.3s ease;
                }
                .step-item.active .step-number {
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    color: white;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.3);
                    transform: scale(1.05);
                }
                .step-item.completed .step-number {
                    background: linear-gradient(135deg, var(--success), #059669);
                    color: white;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
                }
                .step-label {
                    font-size: 0.75rem;
                    margin-top: 0.6rem;
                    color: #64748b;
                    font-weight: 500;
                    text-align: center;
                    transition: color 0.3s;
                }
                .step-item.active .step-label {
                    color: var(--primary);
                    font-weight: 600;
                }
                .step-item.completed .step-label {
                    color: var(--success);
                }
                .step-line {
                    flex: 1;
                    height: 3px;
                    background: #e2e8f0;
                    margin: 0 0.5rem;
                    border-radius: 2px;
                    transition: background 0.3s;
                    align-self: flex-start;
                    margin-top: 20px;
                }
                .step-line.completed {
                    background: linear-gradient(90deg, var(--success), #059669);
                }

                /* ===== Sections ===== */
                .section-title {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1.25rem;
                    padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title h6 {
                    margin: 0;
                    color: var(--primary-dark);
                    font-weight: 600;
                    font-size: 1rem;
                }
                .section-icon {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 0.95rem;
                    box-shadow: 0 2px 6px rgba(41, 90, 167, 0.2);
                }

                /* ===== Inputs modernes ===== */
                .form-label-modern {
                    font-weight: 600;
                    color: #334155;
                    font-size: 0.85rem;
                    margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc;
                    border: 2px solid #e2e8f0;
                    border-right: none;
                    color: var(--primary);
                    font-size: 1rem;
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control {
                    border: 2px solid #e2e8f0;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                .input-modern .form-control:focus + .input-group-text,
                .input-modern .input-group-text:has(+ .form-control:focus) {
                    border-color: var(--secondary);
                }
                .input-modern .input-group-text.bg-light {
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    background: #f1f5f9 !important;
                    color: #64748b;
                    font-size: 0.85rem;
                    font-weight: 600;
                }
                .input-select-modern {
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.6rem 0.9rem;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-select-modern:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }

                /* ===== Cartes type client ===== */
                .type-client-card {
                    flex: 1;
                    padding: 0.75rem 1rem;
                    background: #f8fafc;
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .type-client-card:hover {
                    border-color: var(--secondary);
                    background: #f0fbff;
                }
                .type-client-card.active {
                    border-color: var(--secondary);
                    background: linear-gradient(135deg, rgba(41, 90, 167, 0.05), rgba(33, 172, 205, 0.1));
                    box-shadow: 0 4px 12px rgba(33, 172, 205, 0.15);
                }
                .type-client-card .form-check {
                    margin: 0;
                }
                .type-client-card .form-check-label {
                    font-weight: 600;
                    color: #334155;
                    cursor: pointer;
                }
                .type-client-card .form-check-input:checked {
                    background-color: var(--secondary);
                    border-color: var(--secondary);
                }

                /* ===== Info box ===== */
                .info-box-modern {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    padding: 1rem 1.25rem;
                    background: linear-gradient(135deg, #eff6ff, #f0f9ff);
                    border-left: 4px solid var(--info);
                    border-radius: 10px;
                }
                .info-box-icon {
                    width: 36px;
                    height: 36px;
                    background: rgba(59, 130, 246, 0.15);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--info);
                    font-size: 1.1rem;
                    flex-shrink: 0;
                }
                .info-box-modern strong {
                    color: #1e293b;
                    font-size: 0.9rem;
                }
                .info-box-modern p {
                    color: #64748b;
                }

                /* ===== Boutons modernes ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db;
                    background: white;
                    padding: 0.5rem 1.4rem;
                    border-radius: 50px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary);
                    color: var(--secondary);
                    background: #f0fbff;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33, 172, 205, 0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none;
                    color: white;
                    padding: 0.55rem 2rem;
                    border-radius: 50px;
                    font-weight: 600;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.3);
                    display: inline-flex;
                    align-items: center;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41, 90, 167, 0.4);
                    color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    border: none;
                    color: white;
                    padding: 0.55rem 2rem;
                    border-radius: 50px;
                    font-weight: 600;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
                    display: inline-flex;
                    align-items: center;
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16, 185, 129, 0.4);
                    color: white;
                }
                .btn-modern-success:disabled {
                    opacity: 0.7;
                    transform: none;
                }

                /* ===== Toast moderne ===== */
                .toast-notification-fixed {
                    position: fixed;
                    bottom: 24px;
                    right: 24px;
                    z-index: 9999;
                    animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px;
                    max-width: 400px;
                    overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent;
                    border: none;
                    color: white;
                    cursor: pointer;
                    opacity: 0.8;
                    transition: opacity 0.2s;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom {
                    padding: 16px 20px;
                }
                .toast-body-custom p {
                    color: #1e293b;
                    font-size: 0.9rem;
                }
                .toast-progress {
                    height: 3px;
                    width: 100%;
                    animation: shrink linear forwards;
                }

                /* ===== Animations ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to   { width: 0%; }
                }

                /* ===== Responsive ===== */
                @media (max-width: 768px) {
                    .header-modern {
                        padding: 1rem;
                    }
                    .header-icon-wrapper {
                        width: 44px;
                        height: 44px;
                        font-size: 1.2rem;
                    }
                    .header-title { font-size: 1.1rem; }
                    .step-label { font-size: 0.65rem; }
                    .step-number { width: 36px; height: 36px; font-size: 0.85rem; }
                    .stepper-modern { padding: 1rem; }
                    .toast-notification-fixed {
                        bottom: 12px;
                        right: 12px;
                        left: 12px;
                    }
                    .toast-modern {
                        min-width: auto;
                        max-width: none;
                    }
                    .type-client-card { padding: 0.5rem 0.75rem; }
                }
                .rounded-3 { border-radius: 12px !important; }
                .rounded-pill { border-radius: 50px !important; }
            `}</style>
        </React.Fragment>
    );
}